import { mBinSize } from "models/binSize";
import { EntityKind } from "models/entityKind";
import { IGroupByListColumn, IGroupByListItem, MaxBinsSortOrder } from "models/groupBy";

import { stripTileQualifications } from "components/groupBy/helpers";

type MaxBinDefaultColumn = Maybe<Pick<IGroupByListColumn, "maxNumberSeries">>;

export const MAX_BIN_DEFAULT_SORT_ORDER: MaxBinsSortOrder = "WellCount";

/**
 * The column catalog field for `property`, ignoring tile qualifications
 * (`.dr`, `.pden`) the same way `updateGroupBy.ts` does.
 */
export function findCatalogColumn(
  categoryList: Maybe<IGroupByListItem[]>,
  property: Maybe<string>
): Maybe<IGroupByListColumn> {
  if (!property) return null;
  const fieldKey = stripTileQualifications(property);
  for (const category of categoryList ?? []) {
    const column = category.columns?.find((c) => c.property === fieldKey);
    if (column) return column;
  }
  return null;
}

/**
 * EVA-306: a field's default Max Bins is the catalog's `maxNumberSeries`;
 * 0 or missing means none. Well-only: the sort order is literally "WellCount".
 */
export function getMaxBinDefault(
  column: MaxBinDefaultColumn,
  entityKind: EntityKind = EntityKind.Well
): Maybe<number> {
  if (entityKind !== EntityKind.Well) return null;
  const maxBins = column?.maxNumberSeries;
  return maxBins > 0 ? maxBins : null;
}

/**
 * Returns a new bin with the catalog's `MaxBins`/`MaxBinsSortOrder`, or the
 * same reference when the field has no default.
 */
export function withMaxBinDefault(
  bin: mBinSize,
  column: MaxBinDefaultColumn,
  entityKind: EntityKind = EntityKind.Well
): mBinSize {
  const maxBins = getMaxBinDefault(column, entityKind);
  if (maxBins == null) return bin;
  return {
    ...bin,
    MaxBins: maxBins,
    MaxBinsSortOrder: MAX_BIN_DEFAULT_SORT_ORDER,
    // Clears an axis opt-out too, so a field change returns to the Link to Axis default.
    MaxBinsSortField: undefined,
    MaxBinsLinkToAxis: undefined
  };
}
